import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import ConversationSidebar from '../ConversationSidebar';
import { debugLog, debugWarn, debugError, debugGroupCollapsed } from '../../utils/debug';
import useDrawerGesture from '../../hooks/useDrawerGesture';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import LoadingIndicator from './LoadingIndicator';
import NotificationButton from './NotificationButton';
import * as apiUtils from './apiUtils';
import { getApiUrl } from '../../utils/apiUtils';

// Import DEBUG_ENABLED for manual group management
const isDevelopment = process.env.NODE_ENV === 'development';
const DEBUG_ENABLED = process.env.REACT_APP_DEBUG === 'true' || isDevelopment;

const ENABLE_SSE_DUPLICATE_LOG = false;

const ChatInterface = forwardRef(({ onNavigateToSchedule, onSidebarStateChange, onActiveConversationChange, activeTab }, ref) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [onboardingData, setOnboardingData] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [conversationCreationPromise, setConversationCreationPromise] = useState(null);
  const [promptInfo, setPromptInfo] = useState(null); // Store prompt set info for avatar initials
  
  // 🎯 TRIAGE: Derive stable userId to avoid dependency churn
  const userId = user?.id ?? null;
  
  // Ensure loading state is cleared when component mounts (fixes post-paywall loading issue)
  useEffect(() => {
    setIsLoading(false);
    setIsLoadingOnboardingMessages(false); // Also clear onboarding loading state
  }, []);



  // No longer need localStorage listeners - conversation-based prompt sets are the single source of truth
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  // 🎯 FIX: Track when we just switched conversations to prevent auto-focus
  const [preventAutoFocus, setPreventAutoFocus] = useState(false);
  const preventAutoFocusTimeoutRef = useRef(null); // Track timeout to clear on rapid switches
  // Default sidebar to open on desktop, closed on mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024; // Open on desktop by default
    }
    return false;
  });
  
  // Get current conversation object from activeConversationId
  const currentConversation = activeConversationId 
    ? conversations.find(c => c.id === activeConversationId) 
    : null;

  // Notify parent component when active conversation changes
  useEffect(() => {
    if (onActiveConversationChange && activeConversationId !== null) {
      onActiveConversationChange(activeConversationId);
    }
  }, [activeConversationId, onActiveConversationChange]);

  // Utility function for fetch with retry logic
  const fetchWithRetry = async (url, options = {}, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        return response; // Return on first success
      } catch (error) {
        lastError = error;
        
        // Don't retry on the last attempt
        if (attempt < maxRetries - 1) {
          // Calculate exponential backoff delay: 500ms, 1s, 2s
          const delay = 500 * Math.pow(2, attempt);
          debugLog(`🔄 [FETCH RETRY] Attempt ${attempt + 1}/${maxRetries} failed for ${url}, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed, throw the last error
    throw lastError;
  };

  // Load prompt set information from conversation (single source of truth)
  useEffect(() => {
    const loadPromptInfo = async () => {
      try {
        if (currentConversation && currentConversation.active_prompt_set) {
          // Use the conversation's active prompt set
          setPromptInfo(currentConversation.active_prompt_set);
        } else if (currentConversation && currentConversation.active_prompt_set_id) {
          // Fetch prompt set info if only ID is available
          if (!user || !getToken) {
            debugLog('User not ready for prompt set fetch');
            return;
          }
          const token = await getToken();
          const response = await fetchWithRetry(`${getApiUrl()}/api/prompt-sets`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const promptSetsData = await response.json();
            const activePromptSet = promptSetsData.find(ps => ps.id === currentConversation.active_prompt_set_id);
            if (activePromptSet) {
              setPromptInfo(activePromptSet);
            }
          }
        } else {
          // Fallback: use localStorage for backward compatibility
          if (!user || !getToken) {
            debugLog('User not ready for prompt set fetch');
            return;
          }
          const token = await getToken();
          const response = await fetchWithRetry(`${getApiUrl()}/api/prompt-sets`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const promptSetsData = await response.json();
            const selectedId = localStorage.getItem('selectedPromptSetId');
            let activePromptSet;
            
            if (selectedId && selectedId !== 'null') {
              activePromptSet = promptSetsData.find(ps => ps.id === parseInt(selectedId));
            }
            
            if (!activePromptSet) {
              activePromptSet = promptSetsData.find(ps => ps.is_default) || promptSetsData[0];
            }
            
            setPromptInfo(activePromptSet);
          }
        }
        
        // Note: Auto-switching logic removed to improve performance and eliminate delays
        // Users can manually switch coaches via Prompt Settings if needed
      } catch (error) {
        debugWarn('Failed to load prompt info for avatar:', error);
      }
    };
    
    if (user && getToken) {
      loadPromptInfo();
    }
  }, [user, currentConversation, getToken]);
  
  // Streaming control state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamController, setStreamController] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);
  // isUserTyping state - setIsUserTyping is used in cleanup effects
  // eslint-disable-next-line no-unused-vars
  const [isUserTyping, setIsUserTyping] = useState(false);
  
  const [toastMessage, setToastMessage] = useState(null);
  
  const typingTimeoutRef = useRef(null);
  const userTypingTimeoutRef = useRef(null);
  const resendTimeoutRef = useRef(null);
  const previousConversationIdRef = useRef(null);
  const chatAreaRef = useRef(null);
  const eventSourceRef = useRef(null);
  const scheduleStartTimeRef = useRef(null);  // 🎯 Track when schedule generation started
  
  // Notify parent of sidebar state changes
  useEffect(() => {
    if (onSidebarStateChange) {
      onSidebarStateChange(isSidebarOpen);
    }
  }, [isSidebarOpen, onSidebarStateChange]);

  // Drawer gesture hook — zero React renders during drag
  const { drawerRef, overlayRef, handlers: drawerHandlers, open: openSidebar, close: closeSidebar, toggle: toggleSidebar } = useDrawerGesture(isSidebarOpen, setIsSidebarOpen);

  // Refs to prevent multiple simultaneous API calls
  const fetchingConversationsRef = useRef(false);
  const initializationRef = useRef(null); // Track which userId we've initialized for
  const fetchingMessagesRef = useRef(new Set());
  const [isLoadingOnboardingMessages, setIsLoadingOnboardingMessages] = useState(false);

  // Force create a new conversation (for New Chat button)
  // Defined before fetchConversations because fetchConversations depends on it
  const forceCreateNewConversation = useCallback(async () => {
    debugLog('🆕 [FORCE CREATE] Starting forceCreateNewConversation at:', Date.now());
    
    // If already creating a conversation, return the existing promise
    if (isCreatingConversation && conversationCreationPromise) {
      debugLog('🆕 [FORCE CREATE] Conversation creation already in progress, waiting...');
      return await conversationCreationPromise;
    }
    
    // Create a new promise for conversation creation
    const promise = (async () => {
      // Create AbortController to cancel the request if timeout occurs
      const abortController = new AbortController();
      let timeoutFired = false;
      
      // Add timeout to prevent infinite loading during conversation creation
      const creationTimeout = setTimeout(() => {
        timeoutFired = true;
        debugWarn('🆕 [FORCE CREATE] forceCreateNewConversation taking too long (>20s), cancelling request and clearing loading state');
        abortController.abort(); // Cancel the axios request
        setIsLoading(false);
        setIsCreatingConversation(false);
        setConversationCreationPromise(null); // Clear the promise state
        setError('Request timed out. The server is taking too long to respond. Please try again.');
      }, 20000); // 20 second timeout for conversation creation
      
      try {
        debugLog('🆕 [FORCE CREATE] Setting loading states at:', Date.now());
        setIsCreatingConversation(true);
        setIsLoading(true);
        setError(null); // Clear any previous errors
        
        const selectedPromptSetId = apiUtils.getSelectedPromptSetId();
        debugLog('🆕 [FORCE CREATE] Calling apiUtils.createNewConversation with promptSetId:', selectedPromptSetId, 'at:', Date.now());
        const newConversation = await apiUtils.createNewConversation(user, selectedPromptSetId, abortController.signal);
        
        // Check if timeout already fired (race condition)
        if (timeoutFired) {
          debugWarn('🆕 [FORCE CREATE] Request completed but timeout already fired, ignoring response');
          return null;
        }
        
        debugLog('🆕 [FORCE CREATE] Received conversation response:', newConversation, 'at:', Date.now());
        
        // Create conversation object with empty lastMessage
        const conversationWithLastMessage = {
          ...newConversation,
          lastMessage: newConversation.last_message || null
        };
        
        // Add the new conversation to our list
        setConversations(prev => [conversationWithLastMessage, ...prev]);
        
        // Set it as active
        setActiveConversationId(newConversation.id);
        setMessages([]);
        
        return newConversation.id;
      } catch (err) {
        // Don't show error if timeout already handled it
        if (timeoutFired) {
          debugLog('🆕 [FORCE CREATE] Error after timeout, already handled');
          return null;
        }
        
        // Handle abort error (timeout cancellation)
        if (err.name === 'AbortError' || err.name === 'CanceledError' || (err.code === 'ECONNABORTED')) {
          debugWarn('🆕 [FORCE CREATE] Request was cancelled (likely due to timeout)');
          setError('Request timed out. Please try again.');
          return null;
        }
        
        debugError('Error creating conversation:', err);
        if (err.response && err.response.data && err.response.data.detail) {
          setError(`Error: ${err.response.data.detail}`);
        } else {
          setError('Failed to create a new conversation. Please try again.');
        }
        return null;
      } finally {
        clearTimeout(creationTimeout);
        setIsCreatingConversation(false);
        setIsLoading(false);
        // Clear the promise state after completion (success or failure)
        setConversationCreationPromise(null);
      }
    })();
    
    setConversationCreationPromise(promise);
    return await promise;
  }, [user, isCreatingConversation, conversationCreationPromise]);

  // Fetch conversations with last messages
  // 🎯 TRIAGE: Stable dependencies - use userId (string) instead of user object, remove activeConversationId
  const fetchConversations = useCallback(async () => {
    // Prevent multiple simultaneous calls using ref
    if (fetchingConversationsRef.current) {
      debugLog('💬 [CHAT] fetchConversations already in progress, skipping');
      return;
    }
    
    // 🎯 TRIAGE: Access user from closure - should be available when userId changes
    if (!user || !userId) {
      debugLog('💬 [CHAT] fetchConversations called but user/userId is null', { 
        user: !!user, 
        userId,
        windowWidth: typeof window !== 'undefined' ? window.innerWidth : 'unknown',
        isMobile: typeof window !== 'undefined' ? window.innerWidth < 1024 : 'unknown'
      });
      return;
    }
    
    // 🎯 FIX: Add comprehensive logging for mobile debugging
    debugLog('💬 [CHAT] fetchConversations called', {
      userId,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 'unknown',
      isMobile: typeof window !== 'undefined' ? window.innerWidth < 1024 : 'unknown',
      timestamp: new Date().toISOString()
    });
    
    fetchingConversationsRef.current = true;
    
    // 🎯 FIX: Add timeout to prevent infinite loading
    // Use 35 seconds to allow the API call (30s timeout) to complete or properly timeout
    let timeoutId = null;
    timeoutId = setTimeout(() => {
      if (fetchingConversationsRef.current) {
        // If we already have an active chat, treat this as a soft sidebar refresh failure
        const hasActiveChat = !!activeConversationId || conversations.length > 0;
        debugError('❌ [CHAT] fetchConversations timed out after 35 seconds', {
          hasActiveChat,
          activeConversationId,
          conversationCount: conversations.length
        });

        if (!hasActiveChat) {
          // Initial load failed – show a clear error in the main UI
          setError('Request timed out. Please try refreshing the page.');
        } else {
          // Background refresh failed – keep existing conversations and avoid scaring the user
          debugWarn('⚠️ [CHAT] Sidebar refresh timed out, keeping existing conversations and will rely on next refresh.');
        }

        setIsLoading(false);
        setIsLoadingOnboardingMessages(false);
        fetchingConversationsRef.current = false;
      }
    }, 35000); // 35 second timeout (longer than API timeout of 30s to allow proper error handling)
    
    try {
      setError(null);
      setIsLoading(true);
      
      const conversationsData = await apiUtils.fetchConversations(user);
      
      // Clear timeout on success
      if (timeoutId) clearTimeout(timeoutId);
      
      // 🎯 FIX: Add logging for successful fetch
      debugLog('✅ [CHAT] Successfully fetched conversations', {
        count: conversationsData?.length || 0,
        windowWidth: typeof window !== 'undefined' ? window.innerWidth : 'unknown',
        isMobile: typeof window !== 'undefined' ? window.innerWidth < 1024 : 'unknown'
      });
      
      // 🎯 FIX: Backend now includes last_message in the response for sidebar titles
      // Use the last_message from the backend response if available
      const conversationsWithLastMessage = conversationsData.map((conversation) => ({
        ...conversation,
        lastMessage: conversation.last_message || null // Use last_message from backend for sidebar titles
      }));
      
      setConversations(conversationsWithLastMessage);
      
      // 🎯 FIX: Log state update for debugging
      debugLog('📝 [CHAT] Updated conversations state', {
        count: conversationsWithLastMessage.length,
        activeConversationId
      });
      
      // If we have conversations but no active one, set the one with the most recent message active
      const currentActiveId = activeConversationId;
      if (conversationsWithLastMessage.length > 0 && !currentActiveId) {
        const conversationWithLatestMessage = conversationsWithLastMessage.reduce((latest, current) => {
          const latestTime = latest.last_message_at ? new Date(latest.last_message_at) : null;
          const currentTime = current.last_message_at ? new Date(current.last_message_at) : null;
          
          if (!latestTime && !currentTime) return latest;
          if (!latestTime) return current;
          if (!currentTime) return latest;
          return currentTime > latestTime ? current : latest;
        });
        
        setActiveConversationId(conversationWithLatestMessage.id);
      } else if (conversationsWithLastMessage.length === 0) {
        // Check if onboarding/schedule generation is in progress
        const onboardingConvId = localStorage.getItem(`onboarding_conversation_id_${user.id}`);
        const onboardingJustCompleted = localStorage.getItem(`onboarding_completed_${user.id}`) === 'true';
        const scheduleStartMs = parseInt(localStorage.getItem(`schedule_start_time_${user.id}`) || '0', 10);
        const scheduleRecentlyStarted = scheduleStartMs && (Date.now() - scheduleStartMs < 8000);
        
        if (onboardingConvId || onboardingJustCompleted || scheduleRecentlyStarted) {
          // Wait for conversation to be created
          await new Promise(r => setTimeout(r, 2000));
          try {
            const refreshed = await apiUtils.fetchConversations(user);
            if (refreshed && refreshed.length > 0) {
              setConversations(refreshed.map(c => ({
                ...c,
                lastMessage: c.last_message || null
              })));
              fetchingConversationsRef.current = false;
              return;
            }
          } catch (_) {
            // ignore and fall through to creation
          }
        }
        
        await forceCreateNewConversation();
      }
      
      setIsLoading(false);
    } catch (err) {
      debugGroupCollapsed(`❌ [CHAT] Error fetching conversations for user: ${user?.id}`, () => {
        debugError('❌ [CHAT] Error fetching conversations:', err);
        debugError('❌ [CHAT] Error details:', {
          message: err.message,
          stack: err.stack,
          response: err.response ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          } : 'No response',
          API_URL: getApiUrl(),
          user: user ? { id: user.id } : null
        });
      });
      
      const hasActiveChat = !!activeConversationId || conversations.length > 0;
      if (!hasActiveChat) {
        const errorMessage = err.response 
          ? `Failed to load conversations (${err.response.status})`
          : `Network error: ${err.message || 'Unable to connect to server'}`;
        setError(errorMessage);
      }
      setIsLoading(false);
      setIsLoadingOnboardingMessages(false);
      if (timeoutId) clearTimeout(timeoutId);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      fetchingConversationsRef.current = false;
      setIsLoading(false);
      setIsLoadingOnboardingMessages(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, forceCreateNewConversation]); // 🎯 TRIAGE: Use stable userId instead of user object, remove activeConversationId

  // Listen for conversation refresh events
  useEffect(() => {
    const handleRefreshConversations = () => {
      debugLog('🔄 Refreshing conversations due to coach switch');
      fetchConversations();
    };

    window.addEventListener('runium:refresh-conversations', handleRefreshConversations);
    
    return () => {
      window.removeEventListener('runium:refresh-conversations', handleRefreshConversations);
    };
  }, [fetchConversations]);
  
  // Create a new conversation (idempotent - prevents duplicates)
  const createNewConversation = async () => {
    // If already creating a conversation, return the existing promise
    if (isCreatingConversation && conversationCreationPromise) {
      debugLog('Conversation creation already in progress, waiting...');
      return await conversationCreationPromise;
    }
    
    // If we already have an active conversation, verify it exists before preventing new creation
    if (activeConversationId) {
      // Check if the active conversation actually exists in our conversations list
      const activeConversation = conversations.find(c => c.id === activeConversationId);
      if (activeConversation) {
        debugLog('Active conversation already exists:', activeConversationId);
        return activeConversationId;
      } else {
        debugLog('Active conversation ID exists but conversation not found, clearing it:', activeConversationId);
        setActiveConversationId(null);
        setMessages([]);
      }
    }
    
    // Create a new promise for conversation creation
    const promise = (async () => {
      // Create AbortController to cancel the request if timeout occurs
      const abortController = new AbortController();
      let timeoutFired = false;
      
      // Add timeout to prevent infinite loading during conversation creation
      const creationTimeout = setTimeout(() => {
        timeoutFired = true;
        debugWarn('ChatInterface: createNewConversation taking too long (>20s), cancelling request and clearing loading state');
        abortController.abort(); // Cancel the axios request
        setIsLoading(false);
        setIsCreatingConversation(false);
        setError('Request timed out. The server is taking too long to respond. Please try again.');
      }, 20000); // 20 second timeout for conversation creation
      
      try {
        setIsCreatingConversation(true);
        setIsLoading(true);
        setError(null); // Clear any previous errors
        
        const selectedPromptSetId = apiUtils.getSelectedPromptSetId();
        const newConversation = await apiUtils.createNewConversation(user, selectedPromptSetId, abortController.signal);
        
        // Check if timeout already fired (race condition)
        if (timeoutFired) {
          debugWarn('ChatInterface: Request completed but timeout already fired, ignoring response');
          return null;
        }
        
        // Create conversation object with empty lastMessage
        const conversationWithLastMessage = {
          ...newConversation,
          lastMessage: newConversation.last_message || null
        };
        
        // Add the new conversation to our list
        setConversations(prev => [conversationWithLastMessage, ...prev]);
        
        // Set it as active
        setActiveConversationId(newConversation.id);
        setMessages([]);
        
        return newConversation.id;
      } catch (err) {
        // Don't show error if timeout already handled it
        if (timeoutFired) {
          debugLog('ChatInterface: Error after timeout, already handled');
          return null;
        }
        
        // Handle abort error (timeout cancellation)
        if (err.name === 'AbortError' || err.name === 'CanceledError' || (err.code === 'ECONNABORTED')) {
          debugWarn('ChatInterface: Request was cancelled (likely due to timeout)');
          setError('Request timed out. Please try again.');
          return null;
        }
        
        debugError('Error creating conversation:', err);
        if (err.response && err.response.data && err.response.data.detail) {
          setError(`Error: ${err.response.data.detail}`);
        } else {
          setError('Failed to create a new conversation. Please try again.');
        }
        return null;
      } finally {
        clearTimeout(creationTimeout);
        setIsCreatingConversation(false);
        setIsLoading(false);
        setConversationCreationPromise(null);
      }
    })();
    
    setConversationCreationPromise(promise);
    return await promise;
  };

  // Fetch messages for a specific conversation
  const fetchConversationMessages = useCallback(async (conversationId, skipLoading = false) => {
    if (!conversationId) return null;
    
    // Prevent multiple simultaneous calls for the same conversation
    if (fetchingMessagesRef.current.has(conversationId)) {
      debugLog(`🚀 [CHAT] fetchConversationMessages already in progress for ${conversationId}, skipping`);
      return null;
    }
    
    fetchingMessagesRef.current.add(conversationId);
    
    try {
      if (!skipLoading) {
        setError(null);
        setIsLoading(true);
      }
      
      // Add since parameter if we have a lastMessageTimestamp
      const since = lastMessageTimestamp && skipLoading ? lastMessageTimestamp : null;
      
      const response = await apiUtils.fetchConversationMessages(user, conversationId, since);

      // If we requested with a since parameter and got no messages, it means there are no new messages
      if (lastMessageTimestamp && skipLoading && response.messages.length === 0) {
        return 'no-new-messages';
      }

      // Update the lastMessageTimestamp if available
      if (response.last_message_at) {
        setLastMessageTimestamp(response.last_message_at);
      } else if (response.messages?.length > 0) {
        const newest = response.messages[response.messages.length - 1];
        setLastMessageTimestamp(newest.created_at);
      } else if (skipLoading) {
        // If there are no messages and we're polling, return 'no-new-messages'
        return 'no-new-messages';
      }

      // Messages are already ordered by created_at asc
      const newMessages = response.messages.map(msg => ({
        ...msg,
        is_user: msg.role === 'user'
      }));
      
      // If we have new messages to add
      if (newMessages.length > 0) {
        // If we're typing and user has a pending message, don't replace it
        if (isTyping && messages.length > 0 && messages[messages.length - 1].is_user) {
          // Filter out temp user messages that now exist in the fetched messages
          const userMessageIds = newMessages
            .filter(m => m.is_user)
            .map(m => m.id);
            
          const filteredCurrentMessages = messages.filter(
            m => !m.temp || !m.is_user || !userMessageIds.includes(m.id)
          );
          
          setMessages([...filteredCurrentMessages, ...newMessages]);
          
          if (!skipLoading) {
            setIsLoading(false);
          }
          
          // Update the last message in the conversations list
          updateConversationLastMessage(conversationId, newMessages);
          
          return 'new-messages';
        } else {
          // If we're just adding new messages to existing ones
          if (skipLoading && messages.length > 0) {
            // 🎯 FIX: Check if existing messages belong to the current conversation
            // If they don't, replace them entirely instead of merging
            const firstExistingMsg = messages.find(m => !m.temp && m.conversation_id);
            if (firstExistingMsg && firstExistingMsg.conversation_id !== conversationId) {
              debugLog(`🎯 [FIX] Detected conversation switch from ${firstExistingMsg.conversation_id} to ${conversationId}, replacing messages instead of merging`);
              // Replace all messages with new ones
              setMessages(newMessages);
              updateConversationLastMessage(conversationId, newMessages);
              if (!skipLoading) {
                setIsLoading(false);
              }
              return newMessages.length > 0 ? 'new-messages' : 'no-new-messages';
            }
            
            // Create a map of existing messages by ID for easy lookup
            const existingMessagesMap = new Map(
              messages.filter(m => !m.temp).map(m => [m.id, m])
            );
            
            // Check for both new messages and updated content
            const messagesToAdd = [];
            let hasUpdates = false;
            
            // Build updated messages array
            const updatedMessages = messages.map(existingMsg => {
              // If this is a temp message, keep it as is
              if (existingMsg.temp) return existingMsg;
              
              // Check if we have an updated version of this message
              const updatedMsg = newMessages.find(nm => nm.id === existingMsg.id);
              if (updatedMsg && updatedMsg.content !== existingMsg.content) {
                hasUpdates = true;
                debugLog(`Message ${existingMsg.id} content updated from "${existingMsg.content.substring(0, 50)}..." to "${updatedMsg.content.substring(0, 50)}..."`);
                return updatedMsg;
              }
              return existingMsg;
            });
            
            // Find truly new messages
            newMessages.forEach(newMsg => {
              if (!existingMessagesMap.has(newMsg.id)) {
                messagesToAdd.push(newMsg);
              }
            });
            
            if (messagesToAdd.length > 0 || hasUpdates) {
              // Combine updated existing messages with new messages
              const finalMessages = hasUpdates ? updatedMessages : messages;
              setMessages([...finalMessages, ...messagesToAdd]);
              
              // Update the last message in the conversations list
              if (messagesToAdd.length > 0) {
                updateConversationLastMessage(conversationId, messagesToAdd);
              } else if (hasUpdates) {
                updateConversationLastMessage(conversationId, updatedMessages);
              }
              
              if (!skipLoading) {
                setIsLoading(false);
              }
              return 'new-messages';
            }
          } else {
            // Replace all messages
            // 🎯 DEBUGGING: Log setMessages call
            console.time('setMessages');
            debugLog('🚀 [TIMING] setMessages called with', newMessages.length, 'messages at:', Date.now());
            
            // 🎯 FIX: Replace temporary welcome message with real backend messages
            // Keep the temporary message if no backend welcome message exists yet
            const hasBackendWelcome = newMessages.some(m => m.role === 'assistant');
            const tempWelcomeMessage = messages.find(m => m.id && m.id.toString().startsWith('temp-welcome-'));
            
            let finalMessages = newMessages;
            if (!hasBackendWelcome && tempWelcomeMessage) {
              // Backend welcome not ready yet, keep showing temporary message
              finalMessages = [tempWelcomeMessage, ...newMessages];
              debugLog('🎯 [TEMP] Keeping temporary welcome message until backend welcome arrives');
            } else if (hasBackendWelcome && tempWelcomeMessage) {
              // Backend welcome exists, use backend messages only
              debugLog('🎯 [REPLACE] Replacing temporary welcome with backend messages');
            }
            
            // Set messages once
            setMessages(finalMessages);
            console.timeEnd('setMessages');
            
            // Update the last message in the conversations list
            updateConversationLastMessage(conversationId, newMessages);
            
            // 🎯 FIX: Clear loading state immediately after setMessages
            if (!skipLoading) {
              setIsLoading(false);
              setIsLoadingOnboardingMessages(false); // Clear onboarding loading state
              debugLog('🚀 [FIX] Loading state cleared immediately');
            }
            return newMessages.length > 0 ? 'new-messages' : 'no-new-messages';
          }
        }
      }
      
      if (!skipLoading) {
        setIsLoading(false);
      }
      
      return 'no-new-messages';
    } catch (err) {
      debugError('Error fetching conversation messages:', err);
      if (!skipLoading) {
        setIsLoading(false);
      }
      
      // Handle conversation not found (404) - clear the invalid conversation ID
      if (err.response && err.response.status === 404) {
        debugLog('Conversation not found, clearing active conversation ID:', conversationId);
        setActiveConversationId(null);
        setMessages([]);
        setError(null); // Clear any existing error
        return null;
      }
      
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Error: ${err.response.data.detail}`);
      } else {
        setError('Failed to load messages. Please try again.');
      }
      return null;
    } finally {
      fetchingMessagesRef.current.delete(conversationId);
    }
  }, [user, lastMessageTimestamp, isTyping, messages]);


  // Update the last message for a conversation in the sidebar
  const updateConversationLastMessage = (conversationId, messages) => {
    if (!messages || messages.length === 0) return;
    
    // Find the most recent message
    const sortedMessages = [...messages].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    const newestMessage = sortedMessages[0];
    
    // Update the conversation's last message
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId
          ? { ...conv, lastMessage: newestMessage }
          : conv
      )
    );
  };

  // SSE retry state
  const sseRetryCountRef = useRef(0);
  const sseRetryTimeoutRef = useRef(null);
  const maxSSERetries = 5;
  
  // SSE message batching for cleaner console logs
  const sseInitialLoadRef = useRef(true);
  const sseMessageBatchRef = useRef([]);
  const sseConnectionTimeRef = useRef(null);

  // Server-Sent Events (SSE) for real-time message updates
  const connectSSE = useCallback(async (conversationId, retryCount = 0) => {
    if (!conversationId || !user || !getToken) {
      debugLog('⚠️ [SSE] Cannot connect: missing conversationId, user, or getToken');
      return;
    }

    // Prevent multiple connections to the same conversation
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      debugLog('⚠️ [SSE] Connection already exists, skipping');
      return;
    }

    try {
      // Close existing connection if any
      if (eventSourceRef.current) {
        // Flush any remaining batched messages before closing
        if (sseMessageBatchRef.current.length > 0) {
          console.groupCollapsed(`📨 [SSE] Loaded ${sseMessageBatchRef.current.length} messages on connection`);
          sseMessageBatchRef.current.forEach(msg => {
            debugLog(msg);
          });
          console.groupEnd();
          sseMessageBatchRef.current = [];
        }
        debugLog('🔌 [SSE] Closing existing connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const token = await getToken();
      const API_URL = getApiUrl();
      const url = `${API_URL}/messages/conversations/${conversationId}/stream?token=${token}`;
      
      debugLog(`🔌 [SSE] Connecting to conversation ${conversationId} (attempt ${retryCount + 1}/${maxSSERetries})`);
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        debugLog('✅ [SSE] Connected');
        sseRetryCountRef.current = 0;
        // Reset batching state for new connection
        sseInitialLoadRef.current = true;
        sseMessageBatchRef.current = [];
        sseConnectionTimeRef.current = Date.now();
        
        // After 3 seconds, flush any batched messages and switch to individual logging
        setTimeout(() => {
          if (sseMessageBatchRef.current.length > 0) {
            console.groupCollapsed(`📨 [SSE] Loaded ${sseMessageBatchRef.current.length} messages on connection`);
            sseMessageBatchRef.current.forEach(msg => {
              debugLog(msg);
            });
            console.groupEnd();
            sseMessageBatchRef.current = [];
          }
          sseInitialLoadRef.current = false;
        }, 3000);
      };

      eventSource.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // 🔍 DEBUG: Log prompt router info (for developers)
          if (message.type === 'debug_prompt_info') {
            const emoji = {
              'general_chat': '💬',
              'workout_creation': '🏋️',
              'help_mode': '❓',
              'enhancement': '✨',
              'workout_insertion': '📝'
            }[message.prompt_type] || '🤖';
            
            console.groupCollapsed(`${emoji} [PROMPT ROUTER] ${message.prompt_type.toUpperCase()}`);
            debugLog(`📏 Prompt Size: ${message.prompt_size_kb} KB`);
            debugLog(`🎯 Router Method: ${message.router_method}`);
            debugLog(`💬 User Message: "${message.user_message_preview}"`);
            debugLog(`⏰ Timestamp: ${new Date(message.timestamp).toLocaleTimeString()}`);
            console.groupEnd();
            return; // Don't process as a regular message
          }
          
          // 🚀 LLM Debug events (timing logs)
          if (message.type === 'llm_debug') {
            if (message.event === 'completed') {
              const baseLog = `⏱️ [LLM] ${message.model} - ${message.operation} - Completed (${message.duration.toFixed(1)}s, ${message.message_count} messages)`;
              
              // Show granular timing breakdown if available
              if (message.timing_breakdown) {
                const breakdown = message.timing_breakdown;
                debugLog(baseLog);
                debugLog(`   📊 Timing breakdown:`, {
                  'Context Build': `${breakdown.context_build.toFixed(3)}s`,
                  'LLM API Call': `${breakdown.llm_api.toFixed(3)}s`,
                  'Database Save': `${breakdown.db_save.toFixed(3)}s`,
                  'Total': `${breakdown.total.toFixed(3)}s`
                });
              } else {
                debugLog(baseLog);
              }
            } else if (message.event === 'started') {
              debugLog(`🚀 [LLM] ${message.model} - ${message.operation} - Started`);
            }
            return; // Don't process as a regular message
          }
          
          // Skip if message doesn't have required fields
          if (!message.role || !message.content) {
            return; // Not a valid message, skip processing
          }
          
          // Enhanced logging for different message types
          // Batch initial load messages to reduce console noise
          const isInitialLoad = sseInitialLoadRef.current && 
            sseConnectionTimeRef.current && 
            (Date.now() - sseConnectionTimeRef.current) < 3000;
          
          let logMessage;
          if (message.role === 'system') {
            if (message.content.includes('enhanced with')) {
              logMessage = '📨 [SSE] Enhancement complete: ' + message.content;
            } else if (message.content.includes('schedule is ready')) {
              logMessage = '📨 [SSE] Schedule ready: ' + message.content;
            } else if (message.content.includes('Creating your')) {
              logMessage = '📨 [SSE] Schedule generation started: ' + message.content;
            } else {
              logMessage = '📨 [SSE] System message: ' + message.content;
            }
          } else {
            const preview = message.content ? message.content.substring(0, 50) + '...' : '(no content)';
            logMessage = '📨 [SSE] Coach message: ' + preview;
          }
          
          // Batch initial messages, show individual logs for new messages
          if (isInitialLoad) {
            sseMessageBatchRef.current.push(logMessage);
          } else {
            debugLog(logMessage);
          }

          // Plan button metadata is now stored in database (is_plan_button, plan_data)
          // Legacy messages will be handled by MessageList.js fallback parsing

          setMessages(prev => {
            // Check if message already exists (by ID) - do this FIRST
            const exists = prev.some(m => m.id === message.id);
            if (exists) {
              if (ENABLE_SSE_DUPLICATE_LOG) {
                debugLog('⚠️ [SSE] Message', message.id, 'already exists, skipping');
              }
              return prev;
            }

            // Remove temp messages that this real message replaces
            const filtered = prev.filter(m => {
              // Remove temp welcome if we get real welcome
              if (m.temp && m.role === 'assistant' && message.role === 'assistant') {
                return false;
              }
              return true;
            });

            // Add new message
            return [...filtered, message];
          });

          // Update conversation last message
          if (message.conversation_id) {
            updateConversationLastMessage(message.conversation_id, [message]);
          }

          // 🎯 DEBUG: Log when schedule is actually ready (not just API response)
          if (message.role === 'system' && message.message_source === 'schedule_status' && 
              message.content && message.content.includes('training schedule is ready')) {
            
            // Dispatch event to stop polling in Dashboard
            window.dispatchEvent(new Event('runium:schedule-ready'));
            debugLog('📨 [SCHEDULE] Dispatched schedule-ready event to stop polling');
            
            // 🎯 FIX: Also trigger schedule refresh when "schedule ready" message is detected
            // This is a fallback in case the onboarding_success event isn't received
            debugLog('🔄 [SCHEDULE] Triggering refresh after detecting "schedule ready" message');
            window.dispatchEvent(new Event('runium:refresh-schedule'));
            
            // Calculate actual schedule creation time
            // Check both ref and localStorage to ensure we get the start time
            let startTime = scheduleStartTimeRef.current;
            if (!startTime && user) {
              const storedStartTime = localStorage.getItem(`schedule_start_time_${user.id}`);
              if (storedStartTime) {
                startTime = parseInt(storedStartTime, 10);
                scheduleStartTimeRef.current = startTime; // Update ref for future use
              }
            }
            
            if (startTime) {
              const actualCreationTime = Date.now() - startTime;
              const seconds = (actualCreationTime / 1000).toFixed(1);
              debugLog(`✅ [SCHEDULE GENERATION COMPLETE] Total time: ${actualCreationTime}ms / ${seconds}s`);
            } else {
              debugLog(`✅ [SCHEDULE GENERATION COMPLETE] (timing not available)`);
            }
            debugLog('📅 Check Schedule tab for CORE workouts. Enhancement starting...');
          }

          // 🎯 NEW: Trigger schedule refresh for enhancement messages
          // Check for enhancement completion messages - they come in two formats:
          // 1. System messages with schedule_status source (old format)
          // 2. Assistant messages with "scheduled" source containing "Added detailed coaching notes" (new format)
          // Use flexible regex to catch any enhancement-related message:
          // - "enhanced", "enhance", "Enhanced", "ENHANCED" (case-insensitive)
          // - "improvement", "upgraded"
          // - "Added detailed coaching notes" or "coaching notes"
          // This works regardless of exact message wording
          const isEnhancementComplete = message.content && (
            // Old format: system message with schedule_status
            (message.role === 'system' && 
             message.message_source === 'schedule_status' &&
             /enhanced?|improvement|upgraded/i.test(message.content)) ||
            // New format: assistant message with scheduled source containing coaching notes
            (message.role === 'assistant' &&
             message.message_source === 'scheduled' &&
             /Added detailed coaching notes|coaching notes/i.test(message.content))
          );
          
          if (isEnhancementComplete) {
            
            // Calculate total time from schedule start to enhancement complete
            let totalTimingMessage = '';
            if (scheduleStartTimeRef.current) {
              const totalTime = Date.now() - scheduleStartTimeRef.current;
              const totalSeconds = (totalTime / 1000).toFixed(1);
              totalTimingMessage = ` (total time: ${totalTime}ms / ${totalSeconds}s from start)`;
              
              // Clean up the timing ref
              scheduleStartTimeRef.current = null;
              localStorage.removeItem(`schedule_start_time_${user?.id}`);
            }
            
            debugLog(`✅ [ENHANCEMENT COMPLETE] Workouts enhanced!${totalTimingMessage}`);
            debugLog('🔄 Auto-refreshing schedule tab...');
            setTimeout(() => {
              window.dispatchEvent(new Event('runium:refresh-schedule'));
            }, 1000);
          }
        } catch (err) {
          debugError('❌ [SSE] Parse error:', err);
        }
      };

      eventSource.onerror = (error) => {
        debugError('❌ [SSE] Connection error:', error);
        
        // Close the connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Implement exponential backoff retry logic
        const currentRetryCount = sseRetryCountRef.current;
        
        if (currentRetryCount < maxSSERetries) {
          // Calculate delay with exponential backoff: 1s, 2s, 4s, 8s, 16s
          const delay = Math.min(1000 * Math.pow(2, currentRetryCount), 16000);
          
          debugLog(`🔄 [SSE] Will retry connection in ${delay}ms (attempt ${currentRetryCount + 1}/${maxSSERetries})`);
          
          // Clear any existing retry timeout
          if (sseRetryTimeoutRef.current) {
            clearTimeout(sseRetryTimeoutRef.current);
          }
          
          // Schedule retry
          sseRetryTimeoutRef.current = setTimeout(() => {
            sseRetryCountRef.current = currentRetryCount + 1;
            connectSSE(conversationId, currentRetryCount + 1);
          }, delay);
        } else {
          debugError('❌ [SSE] Max retry attempts reached, giving up');
          debugLog('🔌 [SSE] Connection closed, will reconnect via useEffect if needed');
          sseRetryCountRef.current = 0; // Reset for future attempts
        }
        // Close group on error if it wasn't already closed
        if (DEBUG_ENABLED) {
          console.groupEnd();
        }
      };

    } catch (err) {
      // Close group on setup error
      if (DEBUG_ENABLED) {
        console.groupEnd();
      }
      debugError('❌ [SSE] Setup error:', err);
      
      // Retry on setup error too
      const currentRetryCount = sseRetryCountRef.current;
      if (currentRetryCount < maxSSERetries) {
        const delay = Math.min(1000 * Math.pow(2, currentRetryCount), 16000);
        debugLog(`🔄 [SSE] Setup failed, will retry in ${delay}ms`);
        
        if (sseRetryTimeoutRef.current) {
          clearTimeout(sseRetryTimeoutRef.current);
        }
        
        sseRetryTimeoutRef.current = setTimeout(() => {
          sseRetryCountRef.current = currentRetryCount + 1;
          connectSSE(conversationId, currentRetryCount + 1);
        }, delay);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Removed updateConversationLastMessage to prevent infinite loop

  // Disconnect SSE when component unmounts or conversation changes
  const disconnectSSE = useCallback(() => {
    // Flush any remaining batched messages before disconnecting
    if (sseMessageBatchRef.current.length > 0) {
      console.groupCollapsed(`📨 [SSE] Loaded ${sseMessageBatchRef.current.length} messages on connection`);
      sseMessageBatchRef.current.forEach(msg => {
        debugLog(msg);
      });
      console.groupEnd();
      sseMessageBatchRef.current = [];
    }
    
    if (eventSourceRef.current) {
      debugLog('🔌 [SSE] Disconnecting...');
      try {
        eventSourceRef.current.close();
      } catch (error) {
        debugWarn('🔌 [SSE] Error closing connection:', error);
      }
      eventSourceRef.current = null;
    }
    
    // Clear any pending retry timeouts
    if (sseRetryTimeoutRef.current) {
      clearTimeout(sseRetryTimeoutRef.current);
      sseRetryTimeoutRef.current = null;
    }
    
    // Reset batching state
    sseInitialLoadRef.current = true;
    sseConnectionTimeRef.current = null;
    
    // Reset retry count
    sseRetryCountRef.current = 0;
  }, []);

  // Add temporary message to UI
  const addLocalMessage = (content, isUser, conversationId = null, providedTempId = null) => {
    const tempId = providedTempId || `temp-${Date.now()}`;
    const newMsg = {
      id: tempId,
      tempId: tempId, // Keep stable ID for React key
      content,
      is_user: isUser,
      role: isUser ? 'user' : 'assistant',
      created_at: new Date().toISOString(),
      temp: true,
      conversationId: conversationId || activeConversationId
    };
    setMessages(prevMessages => [...prevMessages, newMsg]);
    return tempId;
  };

  // Handle user typing to interrupt streaming
  // eslint-disable-next-line no-unused-vars
  const handleUserTypingInterrupt = useCallback((value) => {
    // IMMEDIATELY update the message state for responsive typing
    setNewMessage(value);
    
    // Debounce the heavy operations to prevent lag
    if (userTypingTimeoutRef.current) {
      clearTimeout(userTypingTimeoutRef.current);
    }
    
    // Set user typing timeout with shorter delay for better UX
    userTypingTimeoutRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, 500); // Reduced from 1000ms
    
    // Set user typing state
    setIsUserTyping(true);
    
    // Only run stream interruption logic if we're actually streaming
    if (isStreaming && streamController) {
      const messageToResend = pendingMessage; // Store before abort clears it
      
      streamController.abort();
      setIsStreaming(false);
      setIsTyping(false);
      
      // Clear existing resend timeout
      if (resendTimeoutRef.current) {
        clearTimeout(resendTimeoutRef.current);
      }
      
      // Set a timeout to re-send after 10 seconds of no typing
      resendTimeoutRef.current = setTimeout(() => {
        if (messageToResend && !isStreaming) {
          resendMessage(messageToResend);
        }
        setIsUserTyping(false);
      }, 10000);
    } else if (resendTimeoutRef.current && pendingMessage) {
      // If we have a pending resend timeout and user continues typing, reset it
      clearTimeout(resendTimeoutRef.current);
      const messageToResend = pendingMessage;
      
      resendTimeoutRef.current = setTimeout(() => {
        if (messageToResend && !isStreaming) {
          resendMessage(messageToResend);
        }
        setIsUserTyping(false);
      }, 10000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, streamController, pendingMessage]);

  // Re-send a message (for interruption recovery)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resendMessage = async (messageContent) => {
    if (!activeConversationId || isStreaming || !messageContent) return;
    
    // Clear any pending resend timeout to prevent duplicates
    if (resendTimeoutRef.current) {
      clearTimeout(resendTimeoutRef.current);
      resendTimeoutRef.current = null;
    }
    
    // Clear pending message immediately to prevent duplicate calls
    setPendingMessage(null);
    
    try {
      setError(null);
      setIsStreaming(true);
      setIsTyping(true); // Show typing indicator immediately when resending

      // Store the conversation ID at the time of sending to validate callbacks
      const sendingConversationId = activeConversationId;

      // Use the streaming API
      const stream = await apiUtils.streamMessage(
        user,
        activeConversationId,
        messageContent,
        // onMessage - called when a new message is received
        (message) => {
          // Only process if this message belongs to the currently displayed conversation
          if (true) {
            // Only clear typing indicator when we receive an assistant message
            if (message.role === 'assistant' && !message.is_status_update) {
              debugLog('[TYPING] Assistant message received, setting isTyping=false');
              setIsTyping(false);
            }
            setMessages(prevMessages => {
              if (message.replace_temp && message.is_user) {
                // Replace temp user message with the real saved one, but preserve tempId for React key stability
                return prevMessages.map(m => 
                  (m.temp && m.is_user && m.conversationId === sendingConversationId) 
                    ? { ...message, tempId: m.tempId } // Preserve tempId to prevent React remount
                    : m
                );
              } else if (message.is_status_update && message.is_scheduled) {
                // Replace existing scheduled message by ID (for loading -> success/error updates)
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  const updatedMessages = [...prevMessages];
                  updatedMessages[existingIndex] = message;
                  return updatedMessages;
                } else {
                  // If we can't find the message to replace, just add it
                  return [...prevMessages, message];
                }
              } else if (message.is_status_update && message.is_frequency_update) {
                // Replace existing frequency update message by ID (for loading -> success/error updates)
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  const updatedMessages = [...prevMessages];
                  updatedMessages[existingIndex] = message;
                  return updatedMessages;
                } else {
                  // If we can't find the message to replace, just add it
                  return [...prevMessages, message];
                }
              } else if (message.is_status_update && (message.is_run_tool || message.is_regimen_tool)) {
                // Use helper function for tool message deduplication
                return handleToolMessage(prevMessages, message);
              } else if (message.is_scheduled) {
                // For scheduled messages (loading), check if we already have this ID
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  return prevMessages; // Don't add duplicate
                } else {
                  return [...prevMessages, message];
                }
              } else if (message.is_frequency_update) {
                // For frequency update messages (loading), check if we already have this ID
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  return prevMessages; // Don't add duplicate
                } else {
                  return [...prevMessages, message];
                }
              } else if (message.is_run_tool || message.is_regimen_tool) {
                // Use helper function for tool message deduplication
                return handleToolMessage(prevMessages, message);
              } else {
                // Filter out only assistant temp messages, keep user messages as-is
                const filteredMessages = prevMessages.filter(m => 
                  !(m.temp && m.role === 'assistant')
                );
                return [...filteredMessages, message];
              }
            });
            
            // Update conversation last message
            updateConversationLastMessage(sendingConversationId, [message]);
          }
        },
        // onTyping - called when typing indicator should be shown
        () => {
          // Only show typing indicator if this is still the displayed conversation
          if (true) {
            debugLog('[TYPING] onTyping callback called, setting isTyping=true');
            setIsTyping(true);
            // Don't clear typing immediately when messages arrive
          }
        },
        // onError - called when an error occurs
        (errorMessage) => {
          debugError('Streaming error:', errorMessage);
          // Clear typing indicator on error
          debugLog('[TYPING] Error occurred, setting isTyping=false');
          setIsTyping(false);
          setIsStreaming(false);
          setStreamController(null);
          setError(`Error: ${errorMessage}`);
        },
        // onDone - called when streaming is complete
        (completionType) => {
          // Only clear typing if it's still showing (in case no assistant message was received)
          setIsTyping(false);
          setIsStreaming(false);
          setStreamController(null);
          
          // Only clear pending message on natural completion, not abortion
          if (completionType === 'completed') {
            setPendingMessage(null);
          }
          
          // Remove any remaining temp messages
          setMessages(prevMessages => 
            prevMessages.filter(m => !m.temp)
          );
        },
        true // is_resend = true
      );
      
      // Store the controller for potential cancellation
      setStreamController(stream.controller);
      
      // Wait for the stream to complete
      await stream.promise;
      
    } catch (err) {
      debugError('Error re-sending message:', err);
      setIsTyping(false);
      setIsStreaming(false);
      setStreamController(null);
      
      // Handle specific error cases
      if (err.message && err.message.includes('HTTP error! status: 401')) {
        setError('Authentication failed. Please sign out and sign back in.');
      } else if (err.message && err.message.includes('OpenRouter API key not configured')) {
        setError('OpenRouter API key is missing. Please add it to your .env file in the root directory.');
      } else if (err.message && err.message.includes('Failed to get response from OpenRouter API')) {
        setError('Failed to connect to OpenRouter API. Please check your API key and internet connection.');
      } else {
        setError('Failed to send message. Please check if the backend server is running and properly configured.');
      }
    }
  };

  // Send a message
  const handleSendMessage = async (messageContent, options = {}) => {
    // Clear the input field immediately
    setNewMessage('');
    setIsUserTyping(false);
    
    // Clear any pending resend timeouts
    if (resendTimeoutRef.current) {
      clearTimeout(resendTimeoutRef.current);
      resendTimeoutRef.current = null;
    }

    // If user sends a new message while streaming, interrupt and send new one
    if (isStreaming && streamController) {
      streamController.abort();
      setIsStreaming(false);
      setIsTyping(false);
      setStreamController(null);
      
      // Give a brief moment for the abort to complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Ensure we have an active conversation
    let conversationId = activeConversationId;
    if (!conversationId) {
      conversationId = await createNewConversation();
      if (!conversationId) return; // Failed to create conversation
    }

    // Only add user message to UI if this is NOT a button click (execute_plan)
    if (!options.execute_plan) {
      // Create a stable temp ID that will persist when message is replaced
      const tempId = `temp-${Date.now()}`;
      
      // Immediately add user message to UI
      const tempMessage = {
        id: tempId,
        tempId: tempId, // Keep stable ID for React key
        content: messageContent,
        is_user: true,
        role: 'user',
        created_at: new Date().toISOString(),
        temp: true,
        conversationId: conversationId
      };
      
      addLocalMessage(messageContent, true, conversationId, tempId);
      
      // Update the conversation's last message right away for better UX
      updateConversationLastMessage(conversationId, [tempMessage]);
      
      // Store pending message for potential re-sending
      setPendingMessage(messageContent);
    } else {
      debugLog('🎯 [CHAT] Skipping temp user message (execute_plan button click)');
    }
    
    try {
      setError(null);
      setIsStreaming(true);
      setIsTyping(true); // Show typing indicator immediately when sending

      // Store the conversation ID at the time of sending to validate callbacks
      const sendingConversationId = conversationId;
      
      // Use the streaming API
      const stream = await apiUtils.streamMessage(
        user,
        conversationId,
        messageContent,
        // onMessage - called when a new message is received
        (message) => {
          // Only process if this message belongs to the currently displayed conversation
          if (true) {
            // Only clear typing indicator when we receive an assistant message
            if (message.role === 'assistant' && !message.is_status_update) {
              debugLog('[TYPING] Assistant message received, setting isTyping=false');
              setIsTyping(false);
            }
            setMessages(prevMessages => {
              if (message.replace_temp && message.is_user) {
                // Replace temp user message with the real saved one, but preserve tempId for React key stability
                return prevMessages.map(m => 
                  (m.temp && m.is_user && m.conversationId === sendingConversationId) 
                    ? { ...message, tempId: m.tempId } // Preserve tempId to prevent React remount
                    : m
                );
              } else if (message.is_status_update && message.is_scheduled) {
                // Replace existing scheduled message by ID (for loading -> success/error updates)
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  const updatedMessages = [...prevMessages];
                  updatedMessages[existingIndex] = message;
                  return updatedMessages;
                } else {
                  // If we can't find the message to replace, just add it
                  return [...prevMessages, message];
                }
              } else if (message.is_status_update && message.is_frequency_update) {
                // Replace existing frequency update message by ID (for loading -> success/error updates)
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  const updatedMessages = [...prevMessages];
                  updatedMessages[existingIndex] = message;
                  return updatedMessages;
                } else {
                  // If we can't find the message to replace, just add it
                  return [...prevMessages, message];
                }
              } else if (message.is_status_update && (message.is_run_tool || message.is_regimen_tool)) {
                // Use helper function for tool message deduplication
                return handleToolMessage(prevMessages, message);
              } else if (message.is_scheduled) {
                // For scheduled messages (loading), check if we already have this ID
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  return prevMessages; // Don't add duplicate
                } else {
                  return [...prevMessages, message];
                }
              } else if (message.is_frequency_update) {
                // For frequency update messages (loading), check if we already have this ID
                const existingIndex = prevMessages.findIndex(m => m.id === message.id);
                if (existingIndex !== -1) {
                  return prevMessages; // Don't add duplicate
                } else {
                  return [...prevMessages, message];
                }
              } else if (message.is_run_tool || message.is_regimen_tool) {
                // Use helper function for tool message deduplication
                return handleToolMessage(prevMessages, message);
              } else {
                // Filter out only assistant temp messages, keep user messages as-is
                const filteredMessages = prevMessages.filter(m => 
                  !(m.temp && m.role === 'assistant')
                );
                return [...filteredMessages, message];
              }
            });
            
            // Update conversation last message
            updateConversationLastMessage(sendingConversationId, [message]);
          }
        },
        // onTyping - called when typing indicator should be shown
        () => {
          // Only show typing indicator if this is still the displayed conversation
          if (true) {
            debugLog('[TYPING] onTyping callback called, setting isTyping=true');
            setIsTyping(true);
            // Don't clear typing immediately when messages arrive
          }
        },
        // onError - called when an error occurs
        (errorMessage) => {
          debugError('Streaming error:', errorMessage);
          // Clear typing indicator on error
          debugLog('[TYPING] Error occurred, setting isTyping=false');
          setIsTyping(false);
          setIsStreaming(false);
          setStreamController(null);
          setPendingMessage(null);
          setError(`Error: ${errorMessage}`);
        },
        // onDone - called when streaming is complete
        (completionType) => {
          // Only clear typing if it's still showing (in case no assistant message was received)
          setIsTyping(false);
          setIsStreaming(false);
          setStreamController(null);
          
          // Only clear pending message on natural completion, not abortion
          if (completionType === 'completed') {
            setPendingMessage(null);
          }
          
          // Remove any remaining temp messages
          setMessages(prevMessages => 
            prevMessages.filter(m => !m.temp)
          );
        },
        false, // is_resend = false for new messages
        options.execute_plan || false, // execute_plan flag (true when clicking "Create Full Schedule" button)
        options.adjust_plan || false // adjust_plan flag (true when clicking "Adjust Plan" button)
      );
      
      // Store the controller for potential cancellation
      setStreamController(stream.controller);
      
      // Log connection status before starting stream
      apiUtils.logConnectionStatus();
      
      // Wait for the stream to complete
      await stream.promise;
      
      // Log connection status after stream completes
      apiUtils.logConnectionStatus();
      
    } catch (err) {
      debugError('Error sending message:', err);
      setIsTyping(false);
      setIsStreaming(false);
      setStreamController(null);
      setPendingMessage(null);
      
      // Handle specific error cases
      if (err.message && err.message.includes('HTTP error! status: 401')) {
        setError('Authentication failed. Please sign out and sign back in.');
      } else if (err.message && err.message.includes('OpenRouter API key not configured')) {
        setError('OpenRouter API key is missing. Please add it to your .env file in the root directory.');
      } else if (err.message && err.message.includes('Failed to get response from OpenRouter API')) {
        setError('Failed to connect to OpenRouter API. Please check your API key and internet connection.');
      } else {
        setError('Failed to send message. Please check if the backend server is running and properly configured.');
      }
    }
  };

  // Expose sendMessage method to parent component
  useImperativeHandle(ref, () => ({
    sendMessage: handleSendMessage
  }));

  // Start a new chat
  const startNewChat = async () => {
    // Prevent multiple simultaneous calls
    if (isCreatingConversation) {
      debugLog('🆕 [NEW CHAT] Conversation creation already in progress, ignoring click');
      return;
    }
    
    debugLog('🆕 [NEW CHAT] User clicked New Chat button at:', Date.now());
    // Use unique timer name to avoid conflicts when called multiple times
    const timerId = `new-chat-creation_${Date.now()}_${Math.random()}`;
    let startedTimer = false;
    try {
      console.time(timerId);
      startedTimer = true;
    } catch (_) {
      // ignore console timer errors
    }
    
    try {
      const conversationId = await forceCreateNewConversation();
      if (conversationId) {
        debugLog('🆕 [NEW CHAT] Successfully created conversation:', conversationId);
        setActiveConversationId(conversationId);
        // Note: setMessages([]) is handled by the useEffect when activeConversationId changes
        if (startedTimer) {
          try {
            console.timeEnd(timerId);
          } catch (_) {
            // ignore console timer errors
          }
        }
        debugLog('🆕 [NEW CHAT] New chat creation completed at:', Date.now());
        
        // Schedule periodic checks for AI messages (since they're generated in background)
        // SSE will handle message updates for new conversations
        debugLog('🆕 [NEW CHAT] SSE will handle message updates for conversation', conversationId);
      }
    } catch (err) {
      debugError('🆕 [NEW CHAT] Error starting new chat:', err);
      if (startedTimer) {
        try {
          console.timeEnd(timerId);
        } catch (_) {
          // ignore console timer errors
        }
      }
      setError('Failed to start a new chat. Please try again.');
    }
  };
  
  // Delete a conversation
  const deleteConversation = async (conversationId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await apiUtils.deleteConversation(user, conversationId);
      
      // Remove from conversations list and get the updated list
      const updatedConversations = conversations.filter(c => c.id !== conversationId);
      setConversations(updatedConversations);
      
      // If we're deleting the active conversation
      if (activeConversationId === conversationId) {
        // If we have other conversations, select the first one
        if (updatedConversations.length > 0) {
          const nextConversation = updatedConversations[0];
          setActiveConversationId(nextConversation.id);
          // SSE will handle message fetching, no need for regular API call
        } else {
          // No conversations left, create a new one
          await forceCreateNewConversation();
        }
      }
      
      setIsLoading(false);
    } catch (err) {
      debugError('Error deleting conversation:', err);
      setIsLoading(false);
      setError('Failed to delete the conversation. Please try again.');
    }
  };

  // Load conversations when component mounts
  // 🎯 TRIAGE: Split into two effects - one for initial fetch (userId only), one for conversation switching
  useEffect(() => {
    if (!userId) {
      debugLog('⏸️ [CHAT] useEffect: userId not available yet, waiting...');
      return;
    }
    
    // 🎯 FIX: Remove sessionStorage check that might block on mobile
    // Instead, use a ref to track if we've already initialized for this userId
    if (initializationRef.current === userId) {
      debugLog('⏸️ [CHAT] Already initialized for userId:', userId);
      return;
    }
    
    debugLog('🚀 [CHAT] Initializing conversation loading for userId:', userId, {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 'unknown',
      isMobile: typeof window !== 'undefined' ? window.innerWidth < 1024 : 'unknown',
      userAvailable: !!user
    });
    
    initializationRef.current = userId;
    
    const onboardingConvId = localStorage.getItem(`onboarding_conversation_id_${userId}`);
    
    if (onboardingConvId) {
      // Validate onboarding conversation ID with backend before using it
      const tryUseOnboardingConversation = async () => {
          const convId = parseInt(onboardingConvId);
          
          // Check if conversation ID is stale (older than 1 hour) - likely from previous session
          const scheduleStartTime = localStorage.getItem(`schedule_start_time_${userId}`);
          if (scheduleStartTime) {
            const age = Date.now() - parseInt(scheduleStartTime);
            if (age > 3600000) { // 1 hour
              debugLog('🧹 [CHAT] Stale onboarding conversation ID detected, cleaning up');
              localStorage.removeItem(`onboarding_conversation_id_${userId}`);
              localStorage.removeItem(`schedule_start_time_${userId}`);
              localStorage.removeItem(`onboarding_welcome_message_${userId}`);
              localStorage.removeItem(`onboarding_loading_message_${userId}`);
              fetchConversations();
              return;
            }
            scheduleStartTimeRef.current = parseInt(scheduleStartTime);
          }
          
          // Show instant welcome and loading messages if available
          const welcomeMessage = localStorage.getItem(`onboarding_welcome_message_${userId}`);
          const loadingMessage = localStorage.getItem(`onboarding_loading_message_${userId}`);
          
          const tempMessages = [];
          if (welcomeMessage) {
            tempMessages.push({
              id: `temp-welcome-${Date.now()}`,
              role: 'assistant',
              content: welcomeMessage,
              created_at: new Date().toISOString(),
              temp: true
            });
          }
          
          if (loadingMessage) {
            tempMessages.push({
              id: `temp-loading-${Date.now()}`,
              role: 'system',
              content: loadingMessage,
              created_at: new Date(Date.now() + 1000).toISOString(), // 1 second after welcome
              temp: true,
              message_source: 'scheduled'
            });
          }
          
          if (tempMessages.length > 0) {
            setMessages(tempMessages);
          }
          
          try {
            const token = await getToken();
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
            
            const res = await fetch(`${getApiUrl()}/messages/conversations/${convId}`, {
              headers: { 'Authorization': `Bearer ${token}` },
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            if (res.ok) {
              debugLog(`✅ [CHAT] Loaded conversation ${convId}`);
              setActiveConversationId(convId);
              // 🎯 FIX: Also fetch conversations list to populate sidebar
              // The direct conversation load is faster, but sidebar needs the full list
              fetchConversations();
            } else if (res.status === 404) {
              debugLog('🧹 [CHAT] Conversation not found (404), cleaning up stale ID');
              localStorage.removeItem(`onboarding_conversation_id_${userId}`);
              localStorage.removeItem(`schedule_start_time_${userId}`);
              fetchConversations();
            } else {
              debugLog('⚠️ [CHAT] Conversation validation failed, falling back');
              fetchConversations();
            }
          } catch (e) {
            if (e.name === 'AbortError') {
              debugLog('⚠️ [CHAT] Conversation validation timed out, falling back');
            } else {
              debugLog('⚠️ [CHAT] Error validating conversation:', e?.message || e);
            }
            localStorage.removeItem(`onboarding_conversation_id_${userId}`);
            fetchConversations();
          } finally {
            // Cleanup temporary onboarding keys after a short delay
            setTimeout(() => {
              localStorage.removeItem(`onboarding_welcome_message_${userId}`);
              localStorage.removeItem(`onboarding_loading_message_${userId}`);
            }, 2000);
          }
        };
        
      tryUseOnboardingConversation();
    } else {
      // Check if user is currently in onboarding before fetching conversations
      // 🎯 FIX: Make onboarding check non-blocking - always fetch conversations even if check fails
      const checkAndFetchConversations = async () => {
          let isInOnboarding = false;
          
          // Try to check onboarding status, but don't block if it fails
          try {
            const token = await getToken();
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            
            const response = await fetch(`${getApiUrl()}/user/onboarding-status`, {
              headers: { 'Authorization': `Bearer ${token}` },
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            if (response.ok) {
              const data = await response.json();
              isInOnboarding = !data.onboard_complete;
              if (isInOnboarding) {
                debugLog('⏸️ [CHAT] User in onboarding, but fetching conversations anyway for sidebar');
                // 🎯 FIX: Even if in onboarding, fetch conversations to populate sidebar
                // Users may have existing conversations that should be visible
                // Don't return early - continue to fetch
              }
            }
          } catch (e) {
            // 🎯 FIX: Don't block conversation fetching on timeout/error - proceed anyway
            // This is critical for mobile devices where network may be slower
            if (e.name === 'AbortError') {
              debugLog('⚠️ [CHAT] Status check timed out, proceeding with fetch anyway (mobile-friendly)');
            } else {
              debugLog('⚠️ [CHAT] Status check failed, proceeding with fetch anyway:', e.message);
            }
            // Continue to fetch conversations even if check failed
          }
          
          // User completed onboarding (or check failed) - check if we should wait for conversation creation
          const onboardingJustCompleted = localStorage.getItem(`onboarding_completed_${userId}`) === 'true';
          const scheduleStartMs = parseInt(localStorage.getItem(`schedule_start_time_${userId}`) || '0', 10);
          const scheduleRecentlyStarted = scheduleStartMs && (Date.now() - scheduleStartMs < 15000);
          
          if (onboardingJustCompleted && scheduleRecentlyStarted) {
            debugLog('⏳ [CHAT] Waiting for conversation creation...');
            await new Promise(r => setTimeout(r, 3000));
            
            const convId = localStorage.getItem(`onboarding_conversation_id_${userId}`);
            if (convId) {
              debugLog(`✅ [CHAT] Conversation ${convId} ready`);
              setActiveConversationId(parseInt(convId));
              return;
            }
            
            // Re-onboarding case: user might have existing conversations
            fetchConversations(user, 10000);
          } else {
            fetchConversations();
          }
        };
        
      // 🎯 FIX: Add error handling wrapper to ensure conversations are always fetched
      checkAndFetchConversations().catch((error) => {
        debugError('❌ [CHAT] Error in checkAndFetchConversations, falling back to direct fetch:', error);
        // Fallback: directly fetch conversations if the check fails
        if (user && userId) {
          debugLog('🔄 [CHAT] Attempting direct fetchConversations as fallback');
          fetchConversations();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetchConversations]); // 🎯 TRIAGE: Only depend on userId (stable string) and fetchConversations, not activeConversationId

  // Initialize message checker for notifications - DISABLED
  // Since the core status update issue wasn't fixed, messageChecker is not needed
  useEffect(() => {
    // if (user) {
    //   // Start the message checker for notifications
    //   messageChecker.start(user);
    //   
    //   // Don't automatically request permission - iOS requires user gesture
    //   // We'll add a button for this instead
    // }

    // Cleanup on unmount or user change
    // return () => {
    //   messageChecker.stop();
    // };
  }, [user]);

  // When active conversation changes, connect to SSE and fetch initial messages
  useEffect(() => {
      if (activeConversationId) {
      // Conversation switched - connect to SSE
      
      // 🎯 FIX: Only clear messages if we're switching conversations, not on initial load
      // If we have a temp welcome message, keep it until SSE replaces it
      setMessages(prev => {
        const hasTempWelcome = prev.some(m => m.temp && m.role === 'assistant');
        if (hasTempWelcome) {
          // Keep temp welcome until SSE replaces it
          return prev;
        }
        return []; // Clear for conversation switch
      });
      setLastMessageTimestamp(null);
      
      // 🎯 FIX: Fetch initial messages for this conversation
      // SSE only sends NEW messages, not existing ones, so we need to fetch existing messages
      const loadInitialMessages = async () => {
        try {
          setIsLoading(true);
          const result = await fetchConversationMessages(activeConversationId, false);
          if (result === null) {
            debugLog('⏸️ [CHAT] Already loading, skipping');
          }
          setIsLoading(false);
        } catch (err) {
          debugGroupCollapsed(`❌ [CHAT] Error loading conversation ${activeConversationId}`, () => {
            debugError('Error:', err.message);
            if (err.response) {
              debugError(`Status: ${err.response.status} ${err.response.statusText}`);
            }
          });
          setIsLoading(false);
          setError(err.response?.data?.detail || err.message || 'Failed to load messages');
        }
      };
      
      loadInitialMessages();
      
      // Connect to SSE for real-time updates
      connectSSE(activeConversationId);
      
      // Fetch onboarding data for follow-up button logic
      fetchOnboardingData();
      
      // Cleanup: disconnect SSE when conversation changes
    return () => {
        disconnectSSE();
    };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId]); // Removed connectSSE and disconnectSSE from deps to prevent infinite loop
  
  // 📱 MOBILE: Handle visibility changes (reconnect when app comes back to foreground)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeConversationId) {
        // Check if SSE is still connected
        if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
          connectSSE(activeConversationId);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, connectSSE]);


  // Fetch onboarding data
  const fetchOnboardingData = async () => {
    if (!user || !getToken) {
      return;
    }
    
    try {
      const API_URL = getApiUrl();
      const token = await getToken();
      
      const response = await fetchWithRetry(`${API_URL}/user/onboarding-data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        try {
          const result = await response.json();
          setOnboardingData(result.onboarding_data || {});
        } catch (jsonError) {
          debugError('JSON parsing error:', jsonError);
        }
      } else {
        const errorText = await response.text();
        debugLog('Error response body:', errorText);
      }
    } catch (error) {
      debugError('Error fetching onboarding data:', error);
    }
  };

  // Fetch onboarding data when user becomes available
  useEffect(() => {
    if (user && getToken) {
      fetchOnboardingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Removed fetchOnboardingData from deps to prevent infinite loop

  // Separate effect to handle conversation switching cleanup
  useEffect(() => {
    // Only run cleanup if the conversation ID actually changed
    if (activeConversationId && previousConversationIdRef.current !== activeConversationId) {
      // If this is not the first conversation load, do cleanup
      if (previousConversationIdRef.current !== null) {
        // Reset typing and streaming state when switching conversations
        setIsTyping(false);
        setIsUserTyping(false);
        
        // Don't abort ongoing streams - let them continue in the background
        // The conversation ID validation in the callbacks will prevent UI updates
        // in the wrong conversation
        
        // Clear pending message and timeouts when switching conversations
        setPendingMessage(null);
        if (resendTimeoutRef.current) {
          clearTimeout(resendTimeoutRef.current);
          resendTimeoutRef.current = null;
        }
        if (userTypingTimeoutRef.current) {
          clearTimeout(userTypingTimeoutRef.current);
          userTypingTimeoutRef.current = null;
        }
        
        // Clear any temporary messages that don't belong to this conversation
        setMessages(prevMessages => 
          prevMessages.filter(m => !m.temp || m.conversationId === activeConversationId)
        );
      }
      
      // Update the previous conversation ID
      previousConversationIdRef.current = activeConversationId;
    }
  }, [activeConversationId]);

  // Cleanup on unmount - FIXED: Remove streamController dependency to prevent infinite re-runs
  useEffect(() => {
    return () => {
      // Cleanup on unmount (removed verbose logs - expected during refresh)
      
      // Clear all timeouts - refs are captured in variables to avoid stale closure issues
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const typingTimeout = typingTimeoutRef.current;
      const userTypingTimeout = userTypingTimeoutRef.current;
      const resendTimeout = resendTimeoutRef.current;
      
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      if (userTypingTimeout) {
        clearTimeout(userTypingTimeout);
      }
      if (resendTimeout) {
        clearTimeout(resendTimeout);
      }
      if (sseRetryTimeoutRef.current) {
        clearTimeout(sseRetryTimeoutRef.current);
      }
      
      // Abort any ongoing streaming connection
      if (streamController) {
        streamController.abort();
      }
      
      // Disconnect SSE connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on unmount

  // Handle conversation selection with proper desktop/mobile logic
  const selectConversation = async (conversationId) => {
    if (conversationId === activeConversationId) return;
    
    // If user switches conversation while streaming, interrupt the stream
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (isStreaming && streamController) {
      streamController.abort();
      setIsStreaming(false);
      setIsTyping(false);
      setStreamController(null);
    }
    
    // 🎯 FIX: Clear any existing timeout from previous conversation switch
    if (preventAutoFocusTimeoutRef.current) {
      clearTimeout(preventAutoFocusTimeoutRef.current);
      preventAutoFocusTimeoutRef.current = null;
    }
    
    // 🎯 FIX: Prevent auto-focus when switching conversations
    // Set flag immediately to prevent any focus attempts
    setPreventAutoFocus(true);
    
    // Only close sidebar on mobile after selection (before setting new conversation)
    const isMobile = window.innerWidth < 1024;
    if (isMobile && isSidebarOpen) {
      closeSidebar();
    }
    
    // Set new conversation after sidebar closes (prevents focus during transition)
    setActiveConversationId(conversationId);
    
    // Reset preventAutoFocus after a delay to ensure all async operations complete
    // This prevents keyboard popup even if loading takes longer than expected
    // Store timeout ref so we can clear it on rapid switches
    preventAutoFocusTimeoutRef.current = setTimeout(() => {
      setPreventAutoFocus(false);
      preventAutoFocusTimeoutRef.current = null;
    }, 1500); // 1.5 seconds should be enough for messages to load
  };

  // Helper function to handle tool message deduplication
  const handleToolMessage = (prevMessages, message) => {
    // Check for duplicate by ID first
    const existingIndex = prevMessages.findIndex(m => m.id === message.id);
    if (existingIndex !== -1) {
      if (message.is_status_update) {
        // Replace existing message for status updates
        const updatedMessages = [...prevMessages];
        updatedMessages[existingIndex] = message;
        return updatedMessages;
      } else {
        // Don't add duplicate loading messages
        return prevMessages;
      }
    }
    
    // Implement smart grouping for completed actions (not loading)
    if (!message.is_loading) {
      const recentToolMessages = prevMessages.filter(m => 
        (m.is_run_tool || m.is_regimen_tool) && 
        m.role === 'system' &&
        !m.is_loading &&
        new Date(m.created_at).getTime() > Date.now() - 30000 // Within last 30 seconds
      );
      
      if (recentToolMessages.length > 0) {
        // Check if this is the same type of tool action
        const sameToolType = message.is_regimen_tool ? 
          recentToolMessages.filter(m => m.is_regimen_tool) :
          recentToolMessages.filter(m => m.is_run_tool);
        
        if (sameToolType.length >= 2) {
          // Find the last tool message of this type to replace with summary
          const lastToolIndex = prevMessages.length - 1 - prevMessages.slice().reverse().findIndex(m => 
            (message.is_regimen_tool ? m.is_regimen_tool : m.is_run_tool) && 
            m.role === 'system' && 
            !m.is_loading
          );
          
          if (lastToolIndex >= 0) {
            const updatedMessages = [...prevMessages];
            
            // Count successful, error, and total actions
            const successCount = sameToolType.filter(m => !m.is_error).length + (!message.is_error ? 1 : 0);
            const errorCount = sameToolType.filter(m => m.is_error).length + (message.is_error ? 1 : 0);
            const totalCount = sameToolType.length + 1;
            
            // Create summary message
            const summaryMessage = {
              ...message,
              content: message.is_regimen_tool ? 
                `${totalCount > 1 ? '📊' : '✅'} Completed ${totalCount} regimen action${totalCount > 1 ? 's' : ''}: ${successCount} successful${errorCount > 0 ? `, ${errorCount} failed` : ''}` :
                `${totalCount > 1 ? '📊' : '✅'} Completed ${totalCount} tracking action${totalCount > 1 ? 's' : ''}: ${successCount} successful${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
              is_grouped: true,
              grouped_count: totalCount,
              success_count: successCount,
              error_count: errorCount
            };
            
            // Replace the last tool message with summary
            updatedMessages[lastToolIndex] = summaryMessage;
            return updatedMessages;
          }
        }
      }
    }
    
    // Add as normal if no grouping occurred
    return [...prevMessages, message];
  };

  // Track window width for responsive grid columns
  const [windowWidth, setWindowWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);
  
  // Determine grid columns based on sidebar state and window width
  // On desktop (lg+), sidebar takes grid space when open, so we need conditional columns
  // On mobile, sidebar is fixed/overlay, so it doesn't affect grid
  const isDesktop = windowWidth >= 1024;
  const gridColumns = isDesktop && isSidebarOpen ? 'auto 1fr' : '1fr';
  
  return (
    <div 
      className="h-full"
      style={{
        display: 'grid',
        gridTemplateColumns: gridColumns,
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'touch',
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        transition: 'grid-template-columns 0.2s ease-in-out'
      }}
    >
      {/* Sidebar */}
      <ConversationSidebar
        key="conversation-sidebar"
        conversations={conversations}
        activeConversationId={activeConversationId}
        setActiveConversationId={selectConversation}
        isOpen={isSidebarOpen}
        setIsOpen={closeSidebar}
        startNewChat={startNewChat}
        deleteConversation={deleteConversation}
        isLoading={isLoading}
        drawerRef={drawerRef}
        overlayRef={overlayRef}
      />

      {/* Main chat area */}
      <div
        ref={chatAreaRef}
        className="w-full h-full"
        onTouchStart={drawerHandlers.onTouchStart}
        onTouchMove={drawerHandlers.onTouchMove}
        onTouchEnd={drawerHandlers.onTouchEnd}
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Header - Grid Row 1 (auto) */}
        <div style={{ gridRow: '1', overflow: 'hidden' }}>
          <ChatHeader
            onToggleSidebar={toggleSidebar}
            onNewChat={startNewChat}
            isLoading={isLoading}
            isTyping={isTyping}
            messages={messages}
            promptInfo={promptInfo}
          />
        </div>

        {/* Messages - Grid Row 2 (1fr, scrollable) */}
        <div style={{ gridRow: '2', overflow: 'hidden', minHeight: 0 }}>
          <MessageList 
            messages={messages}
            isTyping={isTyping}
            error={error}
            user={user}
            onNavigateToSchedule={onNavigateToSchedule}
            activeTab={activeTab}
            setToastMessage={setToastMessage}
            onSendMessage={handleSendMessage}
            promptInfo={promptInfo}
            onboardingData={onboardingData}
            isLoadingOnboardingMessages={isLoadingOnboardingMessages}
          />
        </div>

        {/* Message Input - Grid Row 3 (auto) */}
        <div 
          data-automation-id="chat-input-container"
          data-testid="chat-input-container"
          style={{ 
          gridRow: '3', 
          overflow: 'hidden',
          // Ensure input stays visible on mobile Chrome
          position: 'relative',
          zIndex: 10
        }}>
          <MessageInput 
            newMessage={newMessage}
            onMessageChange={setNewMessage}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            disabled={false}
            activeTab={activeTab}
            preventAutoFocus={preventAutoFocus}
          />
        </div>

        {/* Loading indicator */}
        <LoadingIndicator isVisible={isLoading} />

        {/* Notification Button */}
        <NotificationButton />

      </div>
    </div>
  );
});

ChatInterface.displayName = 'ChatInterface';

export default ChatInterface; 